import {
  Shield,
  Smartphone,
  Clock,
  Brain,
  Droplets,
  Target,
  CheckCircle2,
  X
} from "lucide-react";

const FEATURES = [
  {
    icon: <Shield className="w-8 h-8" />,
    title: "Patented Innovation",
    description: "First of its kind product where users can detect kidney issues early by testing urine at home.",
    color: "cyan"
  },
  {
    icon: <Smartphone className="w-8 h-8" />,
    title: "Smartphone Detection",
    description: "Use any Android smartphone (9.0+) to detect the colour reaction and get instant results.",
    color: "indigo"
  },
  {
    icon: <Clock className="w-8 h-8" />,
    title: "Quick Results",
    description: "Complete testing within 3 minutes total time. No technical or medical knowledge required.",
    color: "emerald"
  },
  {
    icon: <Brain className="w-8 h-8" />,
    title: "AI/ML Technology",
    description: "Trained on 25,000+ urine samples using specialized AI/ML algorithms for accurate detection.",
    color: "cyan"
  },
  {
    icon: <Droplets className="w-8 h-8" />,
    title: "Minimal Sample",
    description: "Only 2 drops of urine required. Perfect for CKD patients who void less amounts.",
    color: "indigo"
  },
  {
    icon: <Target className="w-8 h-8" />,
    title: "Lab Accuracy",
    description: "95% accuracy compared to traditional lab tests. Reliable and clinically validated.",
    color: "emerald"
  }
];

const COLOR_CLASSES = {
  cyan: { bg: "bg-cyan-50", border: "border-cyan-200", icon: "text-cyan-600", hover: "group-hover:bg-cyan-100" },
  indigo: { bg: "bg-indigo-50", border: "border-indigo-200", icon: "text-indigo-600", hover: "group-hover:bg-indigo-100" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", icon: "text-emerald-600", hover: "group-hover:bg-emerald-100" }
};

export const Features = () => (
  <section id="product" data-testid="features-section" className="relative py-24 md:py-32">
    <div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50 to-white" />

    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <span className="text-cyan-600 text-sm font-mono tracking-widest">Why KidneyCheQ</span>
        <h2 className="font-display text-3xl md:text-5xl font-bold text-slate-800 mt-4 mb-6">
          The Best Solution for <span className="gradient-text">Kidney Health</span>
        </h2>
        <p className="text-slate-600 text-lg">
          At-risk individuals with Diabetes, High Blood Pressure, CKD, Obesity, or above 40 years can now monitor kidney health conveniently at home.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {FEATURES.map((feature) => (
          <div
            key={feature.title}
            data-testid={`feature-card-${feature.title.toLowerCase().replace(/\s/g, '-')}`}
            className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200 p-8 hover:border-slate-300 hover:shadow-lg transition-all duration-300"
          >
            <div className={`w-14 h-14 rounded-xl ${COLOR_CLASSES[feature.color].bg} ${COLOR_CLASSES[feature.color].border} border flex items-center justify-center mb-6 ${COLOR_CLASSES[feature.color].hover} transition-colors`}>
              <div className={COLOR_CLASSES[feature.color].icon}>
                {feature.icon}
              </div>
            </div>
            <h3 className="font-display text-xl font-semibold text-slate-800 mb-3">
              {feature.title}
            </h3>
            <p className="text-slate-600 leading-relaxed">
              {feature.description}
            </p>
          </div>
        ))}
      </div>

      {/* Revolutionary Technology Banner */}
      <div className="mt-16 relative overflow-hidden rounded-3xl bg-gradient-to-r from-orange-50 via-red-50 to-orange-50 border border-orange-200 p-8 md:p-12">
        <div className="absolute top-0 right-0 w-64 h-64 bg-orange-100 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="relative">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 border border-red-200 mb-4">
              <X className="w-4 h-4 text-red-600" />
              <span className="text-red-600 text-sm font-semibold">No More Ancient Strips</span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-800 mb-4">
              Revolutionary Technology, <span className="text-orange-600">Not Outdated Strips</span>
            </h3>
            <p className="text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Traditional urine strips have poor accuracy (37-69% sensitivity*) and are difficult to interpret.
              KidneyCheQ uses <span className="text-cyan-600 font-semibold">AI-powered analysis</span> with
              <span className="text-emerald-600 font-semibold"> 95% accuracy</span> - no guesswork, no color matching by eye.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {/* Traditional Strips */}
            <div className="relative rounded-2xl bg-red-50 border border-red-200 p-5 overflow-hidden">
              <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-red-100 border border-red-200">
                <span className="text-red-600 text-xs font-semibold">OUTDATED</span>
              </div>
              <h4 className="text-red-600 font-semibold mb-3 flex items-center gap-2 text-sm">
                <X className="w-4 h-4" />
                Traditional Strips
              </h4>
              <div className="flex justify-center mb-3">
                <img
                  src="https://customer-assets.emergentagent.com/job_ominar-hub/artifacts/1kapq27b_urine-test-strip-medicalequipmentnig.jpg"
                  alt="Traditional Urine Test Strips with Color Matching Chart"
                  className="h-32 object-contain rounded-lg"
                />
              </div>
              <ul className="space-y-1.5 text-xs">
                <li className="flex items-center gap-2 text-slate-600"><X className="w-3 h-3 text-red-500 flex-shrink-0" /><span>Manual color matching</span></li>
                <li className="flex items-center gap-2 text-slate-600"><X className="w-3 h-3 text-red-500 flex-shrink-0" /><span>37-69% accuracy*</span></li>
                <li className="flex items-center gap-2 text-slate-600"><X className="w-3 h-3 text-red-500 flex-shrink-0" /><span>No digital records</span></li>
              </ul>
            </div>

            {/* Digital Strip Solutions */}
            <div className="relative rounded-2xl bg-amber-50 border border-amber-200 p-5 overflow-hidden">
              <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-200">
                <span className="text-amber-600 text-xs font-semibold">STILL STRIPS</span>
              </div>
              <h4 className="text-amber-600 font-semibold mb-3 flex items-center gap-2 text-sm">
                <Smartphone className="w-4 h-4" />
                Digital Strip Solutions
              </h4>
              <div className="flex justify-center mb-3">
                <div className="relative h-32 flex items-center justify-center">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-16 h-24 text-slate-600" />
                    <span className="text-3xl text-slate-600">+</span>
                    <div className="w-3 h-20 bg-gradient-to-b from-amber-200 via-green-300 to-purple-300 rounded-sm"></div>
                  </div>
                </div>
              </div>
              <ul className="space-y-1.5 text-xs">
                <li className="flex items-center gap-2 text-slate-600"><CheckCircle2 className="w-3 h-3 text-amber-500 flex-shrink-0" /><span>Smartphone based</span></li>
                <li className="flex items-center gap-2 text-slate-600"><X className="w-3 h-3 text-red-500 flex-shrink-0" /><span>Still uses urine strips</span></li>
                <li className="flex items-center gap-2 text-slate-600"><X className="w-3 h-3 text-red-500 flex-shrink-0" /><span>Claims 100% app accuracy</span></li>
              </ul>
              <div className="mt-3 p-2 rounded-lg bg-red-100 border border-red-200">
                <p className="text-red-600 text-xs text-center">
                  <span className="font-semibold">Reality:</span> 100% of 37-69% strip accuracy = <span className="font-bold">Still low overall accuracy!</span>
                </p>
              </div>
            </div>

            {/* KidneyCheQ */}
            <div className="relative rounded-2xl bg-emerald-50 border border-emerald-200 p-5 overflow-hidden">
              <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-200">
                <span className="text-emerald-600 text-xs font-semibold">REVOLUTIONARY</span>
              </div>
              <h4 className="text-emerald-600 font-semibold mb-3 flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4" />
                KidneyCheQ
              </h4>

              <div className="flex gap-2 mb-3 justify-center items-end">
                <div className="text-center">
                  <img src="https://customer-assets.emergentagent.com/job_1488b547-0470-4710-9082-0fb679cd81f3/artifacts/ugz9m83b_Picture1.jpg" alt="KidneyCheQ Device - Before Reaction" className="h-16 w-auto object-contain rounded-md border border-slate-200" />
                  <p className="text-[10px] text-slate-500 mt-1">Before</p>
                </div>
                <div className="text-center">
                  <img src="https://customer-assets.emergentagent.com/job_1488b547-0470-4710-9082-0fb679cd81f3/artifacts/jb1r24jc_Picture2.jpg" alt="KidneyCheQ Device - After Reaction" className="h-16 w-auto object-contain rounded-md border border-slate-200" />
                  <p className="text-[10px] text-slate-500 mt-1">After</p>
                </div>
                <div className="text-center">
                  <img src="https://customer-assets.emergentagent.com/job_1488b547-0470-4710-9082-0fb679cd81f3/artifacts/3f4eppjb_Picture3.jpg" alt="KidneyCheQ App Results" className="h-24 w-auto object-contain rounded-md border border-emerald-300 shadow-md" />
                  <p className="text-[10px] text-emerald-600 font-medium mt-1">Results</p>
                </div>
              </div>
              <ul className="space-y-1.5 text-xs">
                <li className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" /><span>Patented technology</span></li>
                <li className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" /><span>Under regulatory approval stage</span></li>
                <li className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" /><span>No strips - reagent wells</span></li>
                <li className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" /><span>95% lab-grade accuracy</span></li>
              </ul>
            </div>
          </div>

          {/* Accuracy Bar */}
          <div className="mt-8 bg-white rounded-xl p-6 max-w-2xl mx-auto shadow-sm border border-slate-200">
            <h4 className="text-slate-800 font-semibold mb-4 text-center">Accuracy Comparison</h4>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Traditional Urine Strips*</span>
                  <span className="text-red-600">37-69%</span>
                </div>
                <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full" style={{ width: '50%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-amber-600">Digital Strip Solutions</span>
                  <span className="text-amber-600">~85%</span>
                </div>
                <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full" style={{ width: '85%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-cyan-600 font-semibold">KidneyCheQ</span>
                  <span className="text-emerald-600 font-semibold">95%</span>
                </div>
                <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full" style={{ width: '95%' }}></div>
                </div>
              </div>
            </div>
            <p className="text-slate-500 text-xs text-center mt-4">Based on clinical validation studies</p>
          </div>

          {/* Scientific References */}
          <div className="mt-6 max-w-3xl mx-auto bg-slate-50 rounded-xl p-4 border border-slate-200">
            <p className="text-slate-700 text-xs font-semibold mb-2">*Scientific References:</p>
            <ol className="text-slate-600 text-xs space-y-2 list-decimal list-inside">
              <li>
                Defined B, et al. (2024) - "Diagnostic accuracy of point-of-care urine albumin-to-creatinine ratio testing" - <span className="italic">Cochrane Database Syst Rev.</span> - Sensitivity: 37-69%
                <a href="https://pubmed.ncbi.nlm.nih.gov/38262144/" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline ml-1">[PubMed]</a>
              </li>
              <li>
                SAGE Clinical Study (2024) - Dipstick sensitivity 62-70% vs laboratory PCR 94.74% - <span className="italic">SAGE Open Medicine</span>
                <a href="https://journals.sagepub.com/doi/10.1177/26334941241288841" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline ml-1">[SAGE]</a>
              </li>
              <li>
                Shephard MDS, et al. (2023) - "Performance of reagent strip devices for POC testing of proteinuria" - Sensitivity range: 39-78%
                <a href="https://pubmed.ncbi.nlm.nih.gov/36726230/" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline ml-1">[PubMed]</a>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  </section>
);
